import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MegamenuComponent } from '../megamenu/megamenu.component';
import { FooterComponent } from '../footer/footer.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CompanyConfigService, CompanyConfig } from '../../services/company-config.service';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../../amplify/data/resource';

const client = generateClient<Schema>();

@Component({
  selector: 'app-contact-page',
  templateUrl: './contact-page.component.html',
  styleUrl: './contact-page.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MegamenuComponent, 
    FooterComponent, 
    MatButtonModule, 
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ]
})
export class ContactPageComponent {
  config: CompanyConfig;
  leadForm: FormGroup;
  isSubmitting = false;
  formSubmitted = false;

  constructor(
    private companyConfig: CompanyConfigService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {
    this.config = this.companyConfig.getConfig();
    this.leadForm = this.createLeadForm();
  }

  private createLeadForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      phone: ['', [Validators.pattern(/^\d{10}$/)]],
      email: ['', [Validators.email]],
      contactPreference: ['TEXT', [Validators.required]]
    }, { validators: this.contactMethodValidator });
  }

  private contactMethodValidator(form: FormGroup) {
    const phone = form.get('phone')?.value;
    const email = form.get('email')?.value;
    
    if (!phone && !email) {
      return { contactMethodRequired: true };
    }
    return null;
  }

  async onSubmitLead() {
    if (this.leadForm.valid) {
      this.isSubmitting = true;
      
      try {
        const formValue = this.leadForm.value;
        
        // Check for existing leads with same email or phone using secondary indexes
        let isDuplicate = false;
        
        if (formValue.email) {
          const existingEmailLeads = await client.models.Lead.listByEmail({
            email: formValue.email
          });
          if (existingEmailLeads.data && existingEmailLeads.data.length > 0) {
            isDuplicate = true;
          }
        }
        
        if (!isDuplicate && formValue.phone) {
          const existingPhoneLeads = await client.models.Lead.listByPhone({
            phone: formValue.phone
          });
          if (existingPhoneLeads.data && existingPhoneLeads.data.length > 0) {
            isDuplicate = true;
          }
        }

        if (isDuplicate) {
          this.snackBar.open('We already have your information. We\'ll contact you soon!', 'Close', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
          this.formSubmitted = true;
          return;
        }
        
        const leadData = {
          name: formValue.name,
          zipCode: formValue.zipCode,
          phone: formValue.phone || undefined,
          email: formValue.email || undefined,
          contactPreference: formValue.contactPreference,
          status: 'NEW' as const,
          source: 'WEBSITE_CONTACT_FORM',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const result = await client.models.Lead.create(leadData);
        
        if (result.data) {
          this.formSubmitted = true;
          this.snackBar.open('Thank you! We\'ll contact you soon.', 'Close', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
          this.leadForm.reset();
          this.leadForm.patchValue({ contactPreference: 'TEXT' });
        }
      } catch (error) {
        console.error('Error creating lead:', error);
        this.snackBar.open('Sorry, there was an error. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      } finally {
        this.isSubmitting = false;
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.leadForm.controls).forEach(key => {
      const control = this.leadForm.get(key);
      control?.markAsTouched();
    });
  }

  getErrorMessage(fieldName: string): string {
    const control = this.leadForm.get(fieldName);
    if (control?.hasError('required')) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }
    if (control?.hasError('email')) {
      return 'Please enter a valid email address';
    }
    if (control?.hasError('pattern')) {
      if (fieldName === 'zipCode') {
        return 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
      }
      if (fieldName === 'phone') {
        return 'Please enter a valid phone number';
      }
    }
    if (control?.hasError('minlength')) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least 2 characters`;
    }
    if (this.leadForm.hasError('contactMethodRequired')) {
      return 'Please provide either a phone number or email address';
    }
    return '';
  }

  callPhone() {
    this.companyConfig.callPhone();
  }

  sendEmail() {
    this.companyConfig.sendEmail();
  }

  openDirections() {
    this.companyConfig.openDirections();
  }

  formatPhoneNumber(event: any) {
    const input = event.target;
    const value = input.value.replace(/\D/g, ''); // Remove all non-digits
    let formattedValue = '';

    if (value.length > 0) {
      if (value.length <= 3) {
        formattedValue = `(${value}`;
      } else if (value.length <= 6) {
        formattedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`;
      } else {
        formattedValue = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
      }
    }

    // Update the form control with the raw digits for validation
    this.leadForm.patchValue({ phone: value }, { emitEvent: false });
    
    // Update the input display with formatted value
    input.value = formattedValue;
  }

  onPhoneKeyDown(event: KeyboardEvent) {
    // Allow backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }
}